import type { LitSquadEnrichedTransferEntry } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

interface TransferHistoryListProps {
  transfers: LitSquadEnrichedTransferEntry[];
  currentUserId: string;
  loading: boolean;
  hasMore: boolean;
  onLoadMore: () => void;
}

export function TransferHistoryList({
  transfers,
  currentUserId,
  loading,
  hasMore,
  onLoadMore,
}: TransferHistoryListProps) {
  if (loading && transfers.length === 0) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full" />
        ))}
      </div>
    );
  }

  if (transfers.length === 0) {
    return (
      <div className="text-center py-12 text-muted-foreground">
        <p>No transfer history yet.</p>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {transfers.map((t) => {
        const isSender = t.fromUserId === currentUserId;
        const label = describeTransfer(t, isSender);
        return (
          <div
            key={t.transferId}
            className="rounded-md border px-3 py-2 text-sm flex items-center justify-between"
          >
            <div className="flex flex-col">
              <span className="font-medium">{label}</span>
              {t.notes && <span className="text-xs text-muted-foreground">{t.notes}</span>}
            </div>
            <span className="text-xs text-muted-foreground">
              {new Date(t.createdAt).toLocaleString()}
            </span>
          </div>
        );
      })}
      {hasMore && (
        <div className="text-center pt-4">
          <Button variant="outline" size="sm" onClick={onLoadMore} disabled={loading}>
            {loading ? 'Loading...' : 'Load More'}
          </Button>
        </div>
      )}
    </div>
  );
}

function describeTransfer(t: LitSquadEnrichedTransferEntry, isSender: boolean): string {
  const itemName = t.itemName ?? t.itemDefId;
  switch (t.transferType) {
    case 'mint':
      return `Minted ${itemName}`;
    case 'burn':
      return `Burned ${itemName}`;
    case 'legacy_import':
      return `Imported ${itemName} from legacy`;
    case 'admin_grant':
      return `Admin granted ${itemName}`;
    case 'transfer':
    default:
      return isSender
        ? `Sent ${itemName} to ${t.toUsername ?? 'user'}`
        : `Received ${itemName} from ${t.fromUsername ?? 'user'}`;
  }
}
